import { List, Record as record } from 'immutable';

import config from '../../config';
import ValidationMixin from '../ValidationMixin';
import validators from '../../validation';
import { assistanceLocationOther, assistanceReasons } from '../../components/account/passengerAssist/constants';
import { booleanToYesNo } from '../../utils/text';

const customerTitles = config.get('customerTitles') || [];

export const defaultAccessibilityProfile = {
    title: customerTitles[0] || '',
    firstName: '',
    surname: '',
    phone: '',
    postCode: '',
    line1: '',
    line2: '',
    line3: '',
    town: '',
    country: config.get('defaultDeliveryCountry'),
    contactMethod: '',
    assistanceLocation: '',
    otherAssistanceLocation: '',
    reasonForAssistance: new List(),
    otherAssistanceType: '',
    participateSurvey: null,
    participateImprovementSurvey: null,
    pushNotify: null,
    id: null,
};

class AccessibilityProfileForm extends record(defaultAccessibilityProfile) {
    getSchema () {
        const brand = config.get('brand');
        const isSouthEastern = brand === 'southeastern';

        return {
            title: validators.title().required(),
            firstName: validators.name(2, isSouthEastern ? 24 : 50).required(),
            surname: validators.name(2, isSouthEastern ? 24 : 50).required(),
            phone: validators.phone().required(),
            postCode: validators.required(),
            line1: validators.required(),
            town: validators.required(),
            country: validators.required(),
            contactMethod: validators.required(),
        };
    }

    normalizeSaveAccessibilityProfile () {
        const formObj = this.toJS();
        return {
            title: formObj.title,
            firstNames: formObj.firstName,
            surname: formObj.surname,
            contactNumber: formObj.phone,
            postCode: formObj.postCode,
            addressLine1: formObj.line1,
            addressLine2: formObj.line2,
            addressLine3: formObj.line3,
            town: formObj.town,
            county: formObj.country,
            contactMethod: formObj.contactMethod,
            assistanceLocation: formObj.assistanceLocation,
            otherAssistanceLocation: formObj.assistanceLocation === assistanceLocationOther ? this.otherAssistanceLocation : '',
            reasonForAssistance: formObj.reasonForAssistance,
            otherAssistanceType: formObj.reasonForAssistance.includes(assistanceReasons.OTHER.value) ? this.otherAssistanceType : '',
            participateSurvey: booleanToYesNo(formObj.participateSurvey).toLowerCase(),
            participateImprovementSurvey: 'no', // ToDo: remove this field when BE will make it optional https://ontrackretail.atlassian.net/browse/CORE-16129
            pushNotify: formObj.pushNotify,
        };
    }

    arePersonalDetailsValid() {
        return ['title', 'firstName', 'surname', 'phone', 'postCode', 'line1', 'town', 'country', 'contactMethod']
            .every(fieldName => this.isFieldValid(fieldName));
    }

    isAssistanceLocationValid() {
        return Boolean(
            this.get('assistanceLocation') === assistanceLocationOther ? this.get('otherAssistanceLocation') : this.get('assistanceLocation')
        );
    }

    areReasonsForAssistanceValid() {
        const reasonForAssistance = this.get('reasonForAssistance').toJS();
        return Boolean(
            reasonForAssistance.includes(assistanceReasons.OTHER.value) ? this.get('otherAssistanceType') : reasonForAssistance.length
        );
    }

    areAccessibilityProfilePreferencesValid() {
        return ['participateSurvey', 'pushNotify']
            .every(fieldName => typeof this.get(fieldName) === 'boolean');
    }

    areBookingPassengerAssistPreferencesValid() {
        return ['participateSurvey', 'participateImprovementSurvey', 'pushNotify']
            .every(fieldName => typeof this.get(fieldName) === 'boolean');
    }

    isAccessibilityProfileFormValid() {
        return this.arePersonalDetailsValid() && this.isAssistanceLocationValid() && this.areReasonsForAssistanceValid() && this.areAccessibilityProfilePreferencesValid();
    }
}

Object.assign(AccessibilityProfileForm.prototype, ValidationMixin);

export default AccessibilityProfileForm;
